<div class="invoice-form-container" [class.invoice-form-container--type-picker-open]="showDocumentTypePicker">
  <div class="main-card">
    <!-- Card Header -->
    <div class="card-header-custom">
      <div class="header-content">
        <h2 class="header-title">
          <i class="fa" [ngClass]="isEditMode ? 'fa-edit' : 'fa-plus-circle'"></i>
          {{ pageHeadingTitle }}
        </h2>
        <span class="header-badge" *ngIf="isEditMode">
          {{ 'invoices_v2.form.editing_mode' | translate }}
        </span>
      </div>
    </div>

    <form [formGroup]="invoiceForm" (ngSubmit)="$event.preventDefault()">
      <!-- Predicted Document Number -->
      <div class="form-section" *ngIf="predictedDocumentNumber && !isEditMode" style="padding-bottom: 0;">
        <div class="doc-number-alert">
          <div class="alert-icon">
            <i class="fa fa-hashtag"></i>
          </div>
          <div class="alert-content">
            <div class="alert-label">
              {{ 'invoices_v2.form.predicted_document_number' | translate }}
            </div>
            <div class="alert-value">{{ predictedDocumentNumber }}</div>
          </div>
        </div>
      </div>

      <!-- Basic Information Section -->
      <div class="form-section" id="invoice-v2-section-header">
        <div class="section-header">
          <div class="section-icon">
            <i class="fa fa-info-circle"></i>
          </div>
          <div>
            <h3 class="section-title">
              {{ 'invoices_v2.form.basic_information' | translate }}
            </h3>
            <p class="section-subtitle">
              {{ 'invoices_v2.form.basic_information_subtitle' | translate: documentTypeTranslateParams }}
            </p>
          </div>
        </div>

        <!-- Row 1: Company + Customer -->
        <div class="form-grid-2">
          <!-- Company -->
          <div
            class="form-group"
            id="validation-field-company-id"
            data-validation-field="company_id"
            [class.has-validation-error]="hasFieldError('company_id')"
          >
            <label class="form-label">
              {{ 'invoices_v2.common.company' | translate }}
              <span class="required-mark">*</span>
            </label>
            <ng-select
              class="ng-select-custom"
              [class.is-invalid]="hasFieldError('company_id')"
              formControlName="company_id"
              [items]="companies"
              bindValue="company_id"
              bindLabel="displayName"
              [placeholder]="'invoices_v2.form.select_company' | translate"
              [clearable]="false"
              [searchable]="true"
            >
            </ng-select>
            <div *ngIf="hasFieldError('company_id')" class="form-error">
              <i class="fa fa-exclamation-circle"></i>
              {{ 'invoices_v2.form.company_required' | translate }}
            </div>
            <div *ngIf="!hasFieldError('company_id') && invoiceForm.get('company_id')?.invalid && invoiceForm.get('company_id')?.touched" class="form-error">
              <i class="fa fa-exclamation-circle"></i>
              {{ 'invoices_v2.form.company_required' | translate }}
            </div>
          </div>

          <!-- Customer -->
          <div
            class="form-group"
            id="validation-field-customer-id"
            data-validation-field="customer_id"
            [class.has-validation-error]="hasFieldError('customer_id')"
          >
            <label class="form-label">
              {{ 'invoices_v2.common.customer' | translate }}
              <span class="required-mark">*</span>
            </label>
            <div class="customer-input-group">
              <ng-select
                #customerSelect
                class="ng-select-custom ng-select-customer"
                [class.is-invalid]="hasFieldError('customer_id')"
                formControlName="customer_id"
                [items]="customers"
                bindValue="customer_id"
                [placeholder]="'invoices_v2.form.select_customer' | translate"
                [clearable]="false"
                [searchable]="true"
                [searchFn]="customerSearchFn"
              >
                <ng-template ng-option-tmp let-item="item">
                  <div class="customer-option">
                    <span class="customer-option-name">{{ getCustomerName(item) }}</span>
                    <span *ngIf="getCustomerWorkerNumber(item)" class="customer-option-id">{{ getCustomerWorkerNumber(item) }}</span>
                  </div>
                </ng-template>
                <ng-template ng-label-tmp let-item="item">
                  <span class="customer-label-text" [attr.title]="getCustomerName(item) + (getCustomerWorkerNumber(item) ? ' - ' + getCustomerWorkerNumber(item) : '')">{{ getCustomerName(item) }}</span>
                </ng-template>
              </ng-select>
              <button
                *ngIf="invoiceForm.get('company_id')?.value"
                type="button"
                class="btn-add-customer-inline"
                (click)="openAddCustomerModal()"
                [title]="'invoices_v2.form.add_new_customer' | translate"
              >
                <i class="fa fa-plus"></i>
              </button>
            </div>
            <div *ngIf="!invoiceForm.get('company_id')?.value" class="form-helper">
              <i class="fa fa-info-circle"></i>
              {{ 'invoices_v2.form.select_company_first' | translate }}
            </div>
            <div *ngIf="invoiceForm.get('company_id')?.value && customers.length === 0" class="form-helper form-no-customers">
              <i class="fa fa-info-circle"></i>
              {{ 'invoices_v2.form.no_customers' | translate }}
            </div>
            <div *ngIf="hasFieldError('customer_id')" class="form-error">
              <i class="fa fa-exclamation-circle"></i>
              {{ 'invoices_v2.form.customer_required' | translate }}
            </div>
            <div *ngIf="!hasFieldError('customer_id') && invoiceForm.get('customer_id')?.invalid && invoiceForm.get('customer_id')?.touched" class="form-error">
              <i class="fa fa-exclamation-circle"></i>
              {{ 'invoices_v2.form.customer_required' | translate }}
            </div>
            <div *ngIf="showCreateMergeDocumentsButton" class="create-merge-action-row">
              <button
                type="button"
                class="btn-merge-additional-invoices"
                (click)="openCreateMergeFromCustomerButton()"
              >
                <i class="fa fa-compress"></i>
                <span>{{ 'invoices_v2.form.create_merge_add_invoices_btn' | translate }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Row 2: compact meta fields -->
        <div class="form-grid-compact">
          <!-- Invoice Type -->
          <div
            class="form-group"
            id="validation-field-invoice-type"
            data-validation-field="invoice_type"
            [class.has-validation-error]="hasFieldError('invoice_type')"
          >
            <label class="form-label">
              {{ 'invoices_v2.common.invoice_type' | translate }}
              <span class="required-mark">*</span>
            </label>
            <ng-select
              class="ng-select-custom ng-select-compact"
              [class.is-invalid]="hasFieldError('invoice_type')"
              formControlName="invoice_type"
              [clearable]="false"
            >
              <ng-option *ngFor="let type of invoiceTypes" [value]="+type.id">
                {{ curentLang === 'en' ? type.name_en : curentLang === 'ar' ? type.name_ar : type.name_he }}
              </ng-option>
            </ng-select>
          </div>

          <!-- Invoice Date -->
          <div
            class="form-group"
            id="validation-field-invoice-date"
            data-validation-field="invoice_date"
            [class.has-validation-error]="hasFieldError('invoice_date')"
          >
            <label class="form-label">
              {{ 'invoices_v2.common.document_date' | translate }}
              <span class="required-mark">*</span>
            </label>
            <p-calendar
              class="p-calendar-custom p-calendar-compact"
              [class.is-invalid]="hasFieldError('invoice_date')"
              formControlName="invoice_date"
              [showIcon]="true"
              dateFormat="dd/mm/yy"
              [selectOtherMonths]="true"
              appendTo="body"
            ></p-calendar>
          </div>

          <!-- Currency -->
          <div class="form-group" *ngIf="!isReceiptType">
            <label class="form-label">{{ 'invoices_v2.currency.currency' | translate }}</label>
            <ng-select
              class="ng-select-custom ng-select-compact"
              formControlName="currency"
              [clearable]="false"
              [disabled]="!canEditCurrencyFields()"
              [loading]="currenciesLoading"
            >
              <ng-option *ngFor="let c of currencies" [value]="c.currency_code">
                {{ getCurrencyLabel(c) }}
              </ng-option>
            </ng-select>
          </div>

          <!-- Exchange rate (foreign currency only) -->
          <div class="form-group" *ngIf="isForeignCurrencySelected()">
            <label class="form-label">
              {{ 'invoices_v2.currency.exchange_rate' | translate }}
              <span class="form-helper" *ngIf="exchangeRateLoading || foreignConvertLoading">...</span>
            </label>
            <div class="exchange-rate-input-group">
              <input
                type="number"
                class="form-control-custom"
                formControlName="exchange_rate"
                step="0.0001"
                min="0.0001"
                [readOnly]="!canEditCurrencyFields()"
              />
              <button
                type="button"
                class="btn-exchange-rate-refresh"
                (click)="refreshExchangeRate()"
                [disabled]="!canEditCurrencyFields() || exchangeRateLoading"
                [title]="'invoices_v2.currency.refresh_rate' | translate"
                [attr.aria-label]="'invoices_v2.currency.refresh_rate' | translate"
              >
                <i class="fa" [ngClass]="exchangeRateLoading ? 'fa-spinner fa-spin' : 'fa-refresh'"></i>
              </button>
            </div>
            <small
              class="form-helper"
              *ngIf="invoiceForm.get('exchange_rate_date')?.value || invoiceForm.get('exchange_rate_source')?.value"
            >
              <span *ngIf="invoiceForm.get('exchange_rate_date')?.value">
                {{ 'invoices_v2.currency.rate_date' | translate }}:
                {{ invoiceForm.get('exchange_rate_date')?.value }}
              </span>
              <span *ngIf="invoiceForm.get('exchange_rate_source')?.value">
                <ng-container *ngIf="invoiceForm.get('exchange_rate_date')?.value"> · </ng-container>
                {{ 'invoices_v2.currency.rate_source' | translate }}:
                {{ invoiceForm.get('exchange_rate_source')?.value }}
              </span>
            </small>
          </div>

          <!-- Due Date (tax invoice only) -->
          <div class="form-group" *ngIf="showPaymentTrackingFields">
            <label class="form-label">{{ 'invoices_v2.payment.due_date' | translate }}</label>
            <p-calendar
              class="p-calendar-custom p-calendar-compact"
              formControlName="due_date"
              [showIcon]="true"
              dateFormat="dd/mm/yy"
              [selectOtherMonths]="true"
              appendTo="body"
              [showButtonBar]="true"
              [todayButtonStyleClass]="'p-button-text'"
              [clearButtonStyleClass]="'p-button-text'"
            ></p-calendar>
          </div>

          <!-- Payment Status (tax invoice only) -->
          <div class="form-group" *ngIf="showPaymentTrackingFields">
            <label class="form-label">{{ 'invoices_v2.payment.payment_status' | translate }}</label>
            <select formControlName="payment_status" class="form-control-custom">
              <option value="paid">{{ 'invoices_v2.payment.status_paid' | translate }}</option>
              <option value="unpaid">{{ 'invoices_v2.payment.status_unpaid' | translate }}</option>
            </select>
          </div>

          <!-- Language & Tax Status Toggle (hidden for Receipt) -->
          <div class="form-group" *ngIf="!isReceiptType">
            <label class="form-label">&nbsp;</label>
            <button type="button" class="btn-tax-details-toggle" (click)="showTaxDetails = !showTaxDetails">
              <i class="fa" [ngClass]="showTaxDetails ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
              <span>{{ 'invoices_v2.form.additional_information' | translate }}</span>
            </button>
          </div>
        </div>

        <!-- Collapsible Tax Details Section -->
        <div class="tax-details-collapse" *ngIf="!isReceiptType && showTaxDetails">
          <div class="form-grid-compact">
            <!-- Document Language -->
            <div
              class="form-group"
              id="validation-field-document-language"
              data-validation-field="document_language"
              [class.has-validation-error]="hasFieldError('document_language')"
            >
              <label class="form-label">
                {{ 'invoices_v2.form.document_language' | translate }}
                <span class="required-mark">*</span>
              </label>
              <select
                formControlName="document_language"
                class="form-control-custom"
                [class.is-invalid]="hasFieldError('document_language')"
              >
                <option value="ar">العربية</option>
                <option value="en">English</option>
                <option value="he">עברית</option>
              </select>
            </div>

            <!-- Tax Status -->
            <div class="form-group">
              <label class="form-label">
                {{ 'invoices_v2.form.tax_status' | translate }}
              </label>
              <select formControlName="tax_status" class="form-control-custom">
                <option [value]="1">{{ 'invoices_v2.form.subject_to_tax' | translate }}</option>
                <option [value]="0">{{ 'invoices_v2.form.tax_exempt' | translate }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Items Section -->
      <div
        class="form-section items-section"
        id="invoice-v2-section-items"
        data-validation-field="items"
        *ngIf="hasItemsForSelectedType"
      >
        <div class="items-header">
          <div class="section-header" style="margin-bottom: 0; padding-bottom: 0; border-bottom: none;">
            <div class="section-icon" style="background: #10b981;">
              <i class="fa fa-list"></i>
            </div>
            <div>
              <h3 class="section-title">
                {{ 'invoices_v2.form.invoice_items' | translate: documentTypeTranslateParams }}
                <span class="required-mark">*</span>
              </h3>
              <p class="section-subtitle">
                {{ 'invoices_v2.form.invoice_items_subtitle' | translate: documentTypeTranslateParams }}
              </p>
            </div>
          </div>
        </div>

        <div class="items-compact-list" formArrayName="items">
          <div
            class="items-compact-header"
            [attr.dir]="curentLang === 'ar' || curentLang === 'he' ? 'rtl' : 'ltr'"
            aria-hidden="true"
          >
            <span class="items-compact-col items-compact-col-num">#</span>
            <span class="items-compact-col items-compact-col-product">{{ 'invoices_v2.form.product' | translate }}</span>
            <span class="items-compact-col items-compact-col-qty">{{ 'invoices_v2.common.quantity' | translate }}</span>
            <span class="items-compact-col items-compact-col-price">{{ 'invoices_v2.form.unit_price' | translate }}</span>
            <span class="items-compact-col items-compact-col-vat">{{ 'invoices_v2.form.items_col_vat' | translate }}</span>
            <span class="items-compact-col items-compact-col-total">{{ 'invoices_v2.form.items_col_total' | translate }}</span>
            <span class="items-compact-col items-compact-col-actions">{{ 'invoices_v2.form.items_col_actions' | translate }}</span>
          </div>

          <div
            *ngFor="let item of items.controls; let i = index"
            [formGroupName]="i"
            class="item-compact-row"
            [attr.data-item-row]="i"
            [class.item-compact-row--expanded]="isItemExpanded(i)"
            [class.item-compact-row--error]="hasRowError(i)"
            [class.item-compact-row--product-select-open]="productSelectOpenIndex === i"
          >
            <div
              class="item-compact-summary"
              role="button"
              tabindex="0"
              [attr.aria-expanded]="isItemExpanded(i)"
              [attr.aria-label]="getItemDisplayName(i)"
              (click)="toggleItemExpanded(i)"
              (keydown)="onItemSummaryKeydown($event, i)"
            >
              <span class="items-compact-col items-compact-col-num">
                <span class="item-compact-badge">{{ i + 1 }}</span>
              </span>

              <span class="items-compact-col items-compact-col-product">
                <span
                  class="item-compact-name"
                  [attr.dir]="getProductTextDir(getItemDisplayName(i))"
                  [title]="getItemDisplayName(i)"
                >{{ getItemDisplayName(i) }}</span>
                <span
                  class="item-compact-desc"
                  *ngIf="getItemDescription(i) as desc"
                  [attr.dir]="getProductTextDir(desc)"
                  [title]="desc"
                >{{ desc }}</span>
                <span class="item-compact-meta">
                  {{ item.get('quantity')?.value | number: '1.0-2' }}
                  ×
                  {{
                    (isForeignCurrencySelected()
                      ? item.get('price_per_unit_foreign')?.value
                      : item.get('price_per_unit')?.value) | number: '1.2-2'
                  }}
                  ·
                  {{ item.get('vat_rate')?.value }}%
                </span>
              </span>

              <span class="items-compact-col items-compact-col-qty">{{ item.get('quantity')?.value | number: '1.0-2' }}</span>
              <span class="items-compact-col items-compact-col-price">
                {{
                  (isForeignCurrencySelected()
                    ? item.get('price_per_unit_foreign')?.value
                    : item.get('price_per_unit')?.value) | number: '1.2-2'
                }}
              </span>
              <span class="items-compact-col items-compact-col-vat">
                <span class="item-compact-vat-badge">{{ item.get('vat_rate')?.value }}%</span>
              </span>
              <span class="items-compact-col items-compact-col-total">
                <span class="item-compact-total-value">
                  {{
                    (isForeignCurrencySelected()
                      ? item.get('total_amount_foreign')?.value
                      : item.get('total_amount')?.value) | number: '1.2-2'
                  }}
                </span>
              </span>

              <span class="items-compact-col items-compact-col-actions">
                <button
                  type="button"
                  class="btn-item-compact-toggle"
                  (click)="toggleItemExpanded(i); $event.stopPropagation()"
                  [title]="
                    isItemExpanded(i)
                      ? ('invoices_v2.form.items_collapse' | translate)
                      : ('invoices_v2.form.items_expand' | translate)
                  "
                >
                  <i class="fa" [ngClass]="isItemExpanded(i) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                </button>
                <button
                  type="button"
                  class="btn-remove-item btn-remove-item-compact"
                  (click)="removeItem(i); $event.stopPropagation()"
                  [disabled]="items.length === 1"
                  [title]="'invoices_v2.form.remove_item' | translate"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </span>
            </div>

            <div class="item-compact-details" *ngIf="isItemExpanded(i)">
              <div class="item-card-body">
                <div class="item-grid">
                  <!-- Product -->
                  <div
                    class="form-group item-cell-product"
                    [attr.data-validation-field]="'items.' + i + '.product_id'"
                    [class.has-validation-error]="hasFieldError('items.' + i + '.product_id')"
                  >
                    <label class="form-label">
                      {{ 'invoices_v2.form.product' | translate }}
                      <span class="required-mark">*</span>
                    </label>
                    <div class="product-input-group">
                      <ng-select
                        #productSelectRef
                        class="ng-select-custom ng-select-product"
                        [class.is-invalid]="hasFieldError('items.' + i + '.product_id')"
                        formControlName="product_id"
                        [items]="products"
                        bindValue="product_id"
                        [placeholder]="'invoices_v2.form.select_product' | translate"
                        [clearable]="false"
                        [searchable]="true"
                        [searchFn]="productSearchFn"
                        [virtualScroll]="true"
                        [loading]="productsLoading"
                        [trackByFn]="trackProductById"
                        (search)="onProductCatalogSearch($event)"
                        [disabled]="!invoiceForm.get('company_id')?.value"
                        appendTo="body"
                        dropdownPanelClass="invoice-v2-product-dropdown-panel"
                        (open)="onProductSelectOpened(i, productSelectRef)"
                        (close)="onProductSelectClose()"
                      >
                        <ng-template ng-label-tmp let-item="item">
                          <span
                            class="invoice-v2-product-label"
                            dir="auto"
                            [attr.title]="getProductDisplayName(item)"
                          >
                            {{ getProductDisplayName(item) }}
                          </span>
                        </ng-template>
                        <ng-template ng-option-tmp let-item="item">
                          <div class="invoice-v2-product-option" dir="auto">
                            <span class="invoice-v2-product-option-name" [attr.title]="getProductDisplayName(item)">{{ getProductDisplayName(item) }}</span>
                            <span class="invoice-v2-product-option-meta" *ngIf="item.price">
                              {{ formatProductListPrice(item.price) }}
                            </span>
                          </div>
                        </ng-template>
                      </ng-select>
                      <button
                        *ngIf="user_type != 200"
                        type="button"
                        class="btn-add-product-inline"
                        (click)="openAddProductModal(i)"
                        [title]="'invoices_v2.form.add_new_product' | translate"
                      >
                        <i class="fa fa-plus"></i>
                      </button>
                    </div>
                    <div *ngIf="!invoiceForm.get('company_id')?.value" class="form-helper">
                      <i class="fa fa-info-circle"></i>
                      {{ 'invoices_v2.form.select_company_first' | translate }}
                    </div>
                    <div
                      *ngIf="invoiceForm.get('company_id')?.value && !productsLoading && products.length === 0"
                      class="form-helper form-no-products"
                    >
                      <i class="fa fa-info-circle"></i>
                      {{ 'invoices_v2.form.no_products' | translate }}
                    </div>
                  </div>

                  <!-- Product Description -->
                  <div
                    class="form-group item-cell-description"
                    [attr.data-validation-field]="'items.' + i + '.description'"
                    [class.has-validation-error]="hasFieldError('items.' + i + '.description')"
                  >
                    <label class="form-label">
                      {{ 'invoices_v2.form.product_desc' | translate }}
                    </label>
                    <textarea
                      formControlName="description"
                      class="form-control-custom item-description-textarea"
                      [class.is-invalid]="hasFieldError('items.' + i + '.description')"
                      rows="2"
                      maxlength="300"
                      [placeholder]="'invoices_v2.form.enter_description' | translate"
                    ></textarea>
                  </div>

                  <!-- Quantity -->
                  <div
                    class="form-group"
                    [attr.data-validation-field]="'items.' + i + '.quantity'"
                    [class.has-validation-error]="hasFieldError('items.' + i + '.quantity')"
                  >
                    <label class="form-label">
                      {{ 'invoices_v2.common.quantity' | translate }}
                      <span class="required-mark">*</span>
                    </label>
                    <input
                      type="number"
                      formControlName="quantity"
                      class="form-control-custom"
                      [class.is-invalid]="hasFieldError('items.' + i + '.quantity')"
                      step="0.01"
                      min="0.01"
                      (focus)="selectAllOnFocus($event)"
                      (keydown.enter)="selectAllOnFocus($event)"
                    />
                  </div>

                  <!-- Price + Include VAT -->
                  <div
                    class="form-group item-cell-unit-price"
                    [attr.data-validation-field]="'items.' + i + '.price_per_unit'"
                    [class.has-validation-error]="hasFieldError('items.' + i + '.price_per_unit')"
                  >
                    <label class="form-label">
                      {{ 'invoices_v2.form.unit_price' | translate }}
                      <span *ngIf="isForeignCurrencySelected()">({{ getSelectedCurrencySymbol() }})</span>
                      <span class="required-mark">*</span>
                      <span class="vat-included-badge" *ngIf="item.get('isVatIncluded')?.value">
                        {{ 'invoices_v2.form.with_vat' | translate }}
                      </span>
                    </label>
                    <div class="price-with-vat-checkbox">
                      <input
                        *ngIf="!isForeignCurrencySelected()"
                        type="number"
                        formControlName="price_per_unit"
                        class="form-control-custom"
                        [class.is-invalid]="hasFieldError('items.' + i + '.price_per_unit')"
                        step="0.01"
                        min="0"
                        (focus)="selectAllOnFocus($event)"
                        (keydown.enter)="selectAllOnFocus($event)"
                      />
                      <input
                        *ngIf="isForeignCurrencySelected()"
                        type="number"
                        formControlName="price_per_unit_foreign"
                        class="form-control-custom"
                        [class.is-invalid]="hasFieldError('items.' + i + '.price_per_unit')"
                        step="0.01"
                        min="0"
                        (focus)="selectAllOnFocus($event)"
                        (keydown.enter)="selectAllOnFocus($event)"
                      />
                      <label class="vat-checkbox-label">
                        <input
                          type="checkbox"
                          (change)="onVatCheckboxChange(i, $event)"
                          [checked]="item.get('isVatIncluded')?.value"
                        />
                        {{ 'invoices_v2.form.including_vat' | translate }}
                      </label>
                    </div>
                  </div>

                  <!-- Discount -->
                  <div
                    class="form-group"
                    [attr.data-validation-field]="'items.' + i + '.discount'"
                    [class.has-validation-error]="hasFieldError('items.' + i + '.discount')"
                  >
                    <label class="form-label">
                      {{ 'invoices_v2.common.discount' | translate }}
                      <span *ngIf="isForeignCurrencySelected()">({{ getSelectedCurrencySymbol() }})</span>
                    </label>
                    <input
                      *ngIf="!isForeignCurrencySelected()"
                      type="number"
                      formControlName="discount"
                      class="form-control-custom"
                      [class.is-invalid]="hasFieldError('items.' + i + '.discount')"
                      step="0.01"
                      min="0"
                      (focus)="selectAllOnFocus($event)"
                      (keydown.enter)="selectAllOnFocus($event)"
                    />
                    <input
                      *ngIf="isForeignCurrencySelected()"
                      type="number"
                      formControlName="discount_foreign"
                      class="form-control-custom"
                      [class.is-invalid]="hasFieldError('items.' + i + '.discount')"
                      step="0.01"
                      min="0"
                      (focus)="selectAllOnFocus($event)"
                      (keydown.enter)="selectAllOnFocus($event)"
                    />
                  </div>
                </div>
              </div>

              <div class="item-card-footer">
                <div class="item-summary">
                  <span class="summary-label">{{ 'invoices_v2.form.sub_total' | translate }}</span>
                  <span class="summary-value">{{ item.get('sub_total')?.value | number: '1.2-2' }}</span>
                </div>
                <div class="item-summary">
                  <span class="summary-label">{{ 'invoices_v2.form.vat' | translate }}</span>
                  <span class="summary-value">{{ item.get('vat_amount')?.value | number: '1.2-2' }}</span>
                  <span class="vat-percentage-badge">{{ item.get('vat_rate')?.value }}%</span>
                </div>
                <div class="item-summary item-total">
                  <span class="summary-label">{{ 'invoices_v2.form.total' | translate }}</span>
                  <span class="summary-value">{{ item.get('total_amount')?.value | number: '1.2-2' }}</span>
                </div>
              </div>
            </div>
          </div>

          <div
            *ngIf="items.length > 0"
            class="items-compact-footer"
            [class.items-compact-footer--foreign]="isForeignCurrencySelected()"
            [attr.dir]="curentLang === 'ar' || curentLang === 'he' ? 'rtl' : 'ltr'"
          >
            <div class="items-compact-footer-item">
              <span class="items-compact-footer-label">{{ 'invoices_v2.form.items_footer_before_vat' | translate }}</span>
              <span class="items-compact-footer-value">
                <ng-container *ngIf="isForeignCurrencySelected(); else ilsBeforeVat">
                  {{ foreignCalculatedAmounts.amount_after_discount | number: '1.2-2' }} {{ getSelectedCurrencySymbol() }}
                  <small class="items-compact-footer-ils">({{ calculatedAmounts.amount_after_discount | number: '1.2-2' }} ₪)</small>
                </ng-container>
                <ng-template #ilsBeforeVat>{{ calculatedAmounts.amount_after_discount | number: '1.2-2' }}</ng-template>
              </span>
            </div>
            <div class="items-compact-footer-item">
              <span class="items-compact-footer-label">{{ 'invoices_v2.form.items_footer_vat' | translate }}</span>
              <span class="items-compact-footer-value">
                <ng-container *ngIf="isForeignCurrencySelected(); else ilsVat">
                  {{ foreignCalculatedAmounts.all_vat_amount | number: '1.2-2' }} {{ getSelectedCurrencySymbol() }}
                  <small class="items-compact-footer-ils">({{ calculatedAmounts.all_vat_amount | number: '1.2-2' }} ₪)</small>
                </ng-container>
                <ng-template #ilsVat>{{ calculatedAmounts.all_vat_amount | number: '1.2-2' }}</ng-template>
              </span>
            </div>
            <div class="items-compact-footer-item items-compact-footer-item--total">
              <span class="items-compact-footer-label">{{ 'invoices_v2.form.items_footer_total' | translate }}</span>
              <span class="items-compact-footer-value">
                <ng-container *ngIf="isForeignCurrencySelected(); else ilsTotal">
                  {{ foreignCalculatedAmounts.payment_amount_including_vat | number: '1.2-2' }} {{ getSelectedCurrencySymbol() }}
                  <small class="items-compact-footer-ils">({{ calculatedAmounts.payment_amount_including_vat | number: '1.2-2' }} ₪)</small>
                </ng-container>
                <ng-template #ilsTotal>{{ calculatedAmounts.payment_amount_including_vat | number: '1.2-2' }}</ng-template>
              </span>
            </div>
          </div>
        </div>

        <!-- Add Item button -->
        <div class="items-add-row">
          <button type="button" class="btn-add-item" (click)="addItem()">
            <i class="fa fa-plus"></i>
            {{ 'invoices_v2.form.add_item' | translate }}
          </button>
        </div>

        <!-- Empty Items Alert -->
        <div *ngIf="items.length === 0" class="empty-items-alert">
          <i class="fa fa-exclamation-triangle"></i>
          <p>{{ 'invoices_v2.form.add_at_least_one_item' | translate: documentTypeTranslateParams }}</p>
        </div>
      </div>

      <!-- Payment Methods Section -->
      <div
        class="form-section payment-section"
        id="invoice-v2-section-payments"
        data-validation-field="payment_methods"
        *ngIf="paymentMethodsRequired"
      >
        <div class="items-header">
          <div class="section-header" style="margin-bottom: 0; padding-bottom: 0; border-bottom: none;">
            <div class="section-icon" style="background: #f59e0b;">
              <i class="fa fa-credit-card"></i>
            </div>
            <div>
              <h3 class="section-title">
                {{ 'invoices_v2.form.payment_methods' | translate }}
                <span class="required-mark">*</span>
              </h3>
              <p class="section-subtitle">
                {{ 'invoices_v2.form.payment_methods_subtitle' | translate: documentTypeTranslateParams }}
              </p>
            </div>
          </div>
        </div>

        <div formArrayName="payment_methods">
          <div
            *ngFor="let payment of paymentMethods.controls; let i = index"
            [formGroupName]="i"
            [id]="'invoice-v2-payment-card-' + i"
            [attr.data-payment-row]="i"
            class="payment-card"
            [class.payment-card--error]="hasPaymentError(i)"
            [class.payment-card--highlight]="isPaymentHighlighted(i)"
          >
            <div class="payment-card-header">
              <div class="payment-type-icon">
                <i class="fa" [ngClass]="getPaymentMethodIcon(payment.get('type')?.value)"></i>
              </div>
              <button
                type="button"
                class="btn-remove-payment"
                (click)="removePaymentMethod(i)"
                [title]="'invoices_v2.form.remove_payment' | translate"
              >
                <i class="fa fa-trash"></i>
              </button>
            </div>

            <div class="payment-card-body">
              <div class="payment-grid">
                <!-- Payment Type -->
                <div
                  class="form-group"
                  [attr.data-validation-field]="'payment_methods.' + i + '.type'"
                  [class.has-validation-error]="hasFieldError('payment_methods.' + i + '.type')"
                >
                  <label class="form-label">
                    {{ 'invoices_v2.form.payment_type' | translate }}
                    <span class="required-mark">*</span>
                  </label>
                  <select
                    formControlName="type"
                    class="form-control-custom"
                    [attr.data-payment-type-select]="i"
                    [class.is-invalid]="hasFieldError('payment_methods.' + i + '.type')"
                  >
                    <ng-container *ngFor="let pm of paymentMethodsList">
                      <option [value]="pm.payment_method_code">
                        {{
                          curentLang === 'en'
                            ? pm.payment_method_name_en
                            : curentLang === 'ar'
                              ? pm.payment_method_name_ar
                              : pm.payment_method_name_he
                        }}
                      </option>
                    </ng-container>
                  </select>
                </div>

                <!-- Date -->
                <div
                  [formGroup]="payment.get('details')"
                  class="form-group"
                  [attr.data-validation-field]="'payment_methods.' + i + '.details.date'"
                  [class.has-validation-error]="hasFieldError('payment_methods.' + i + '.details.date')"
                >
                  <label class="form-label">
                    {{ 'invoices_v2.common.date' | translate }}
                    <span class="required-mark">*</span>
                  </label>
                  <p-calendar
                    class="p-calendar-custom"
                    [class.is-invalid]="hasFieldError('payment_methods.' + i + '.details.date')"
                    formControlName="date"
                    [showIcon]="true"
                    dateFormat="dd/mm/yy"
                    [selectOtherMonths]="true"
                    appendTo="body"
                    [placeholder]="'invoices_v2.form.select_date' | translate"
                  ></p-calendar>
                  <div
                    *ngIf="hasFieldError('payment_methods.' + i + '.details.date')"
                    class="form-error"
                  >
                    <i class="fa fa-exclamation-circle"></i>
                    {{ 'invoices_v2.form.date_required' | translate }}
                  </div>
                  <div
                    *ngIf="
                      !hasFieldError('payment_methods.' + i + '.details.date') &&
                      payment.get('details')?.get('date')?.invalid &&
                      payment.get('details')?.get('date')?.touched
                    "
                    class="form-error"
                  >
                    <i class="fa fa-exclamation-circle"></i>
                    {{ 'invoices_v2.form.date_required' | translate }}
                  </div>
                </div>

                <!-- Amount -->
                <div
                  class="form-group"
                  [attr.data-validation-field]="'payment_methods.' + i + '.amount'"
                  [class.has-validation-error]="hasFieldError('payment_methods.' + i + '.amount')"
                >
                  <label class="form-label">
                    {{ 'invoices_v2.common.amount' | translate }}
                    <span class="required-mark">*</span>
                  </label>
                  <input
                    type="number"
                    formControlName="amount"
                    class="form-control-custom"
                    [class.is-invalid]="hasFieldError('payment_methods.' + i + '.amount')"
                    step="0.01"
                    min="0.01"
                    (focus)="selectAllOnFocus($event)"
                    (keydown.enter)="selectAllOnFocus($event)"
                  />
                  <div
                    *ngIf="hasFieldError('payment_methods.' + i + '.amount')"
                    class="form-error"
                  >
                    <i class="fa fa-exclamation-circle"></i>
                    {{ 'invoices_v2.form.amount_required' | translate }}
                  </div>
                  <div
                    *ngIf="
                      !hasFieldError('payment_methods.' + i + '.amount') &&
                      payment.get('amount')?.invalid &&
                      payment.get('amount')?.touched
                    "
                    class="form-error"
                  >
                    <i class="fa fa-exclamation-circle"></i>
                    {{ 'invoices_v2.form.amount_required' | translate }}
                  </div>
                </div>
              </div>

              <!-- Details group: dynamic required_fields -->
              <div formGroupName="details">

                <!-- Payment Details (Dynamic based on requires_details and required_fields) -->
                <div
                  *ngIf="requiresDetails(payment.get('type')?.value)"
                  class="payment-details-section"
                  style="margin-top: 20px; padding-top: 20px; border-top: 1px solid #e5e7eb;"
                >
                  <h4
                    [id]="'invoice-v2-payment-details-' + i"
                    style="margin-bottom: 16px; font-size: 0.9rem; color: #6b7280;"
                  >
                    {{ 'invoices_v2.form.payment_details' | translate }}
                  </h4>
                  <div class="payment-details-grid">
                    <!-- Check number (reference field for Check payment type) -->
                    <div
                      *ngIf="isFieldRequired(payment.get('type')?.value, 'reference')"
                      class="form-group"
                      [attr.data-validation-field]="'payment_methods.' + i + '.details.reference'"
                      [class.has-validation-error]="hasFieldError('payment_methods.' + i + '.details.reference')"
                    >
                      <label class="form-label" [attr.for]="'invoice-v2-check-ref-' + i">
                        {{ 'invoices_v2.form.check_number_label' | translate }}
                        <span class="required-mark">*</span>
                      </label>
                      <input
                        type="text"
                        [id]="'invoice-v2-check-ref-' + i"
                        formControlName="reference"
                        class="form-control-custom"
                        [placeholder]="'invoices_v2.form.enter_check_number' | translate"
                      />
                      <div
                        *ngIf="
                          payment.get('details')?.get('reference')?.invalid &&
                          payment.get('details')?.get('reference')?.touched
                        "
                        class="form-error"
                      >
                        <i class="fa fa-exclamation-circle"></i>
                        {{ 'invoices_v2.form.check_number_required' | translate }}
                      </div>
                    </div>

                    <!-- Bank -->
                    <div
                      *ngIf="isBankDetailFieldVisible(payment.get('type')?.value, 'bank')"
                      class="form-group"
                      [attr.data-validation-field]="'payment_methods.' + i + '.details.bank'"
                      [class.has-validation-error]="hasFieldError('payment_methods.' + i + '.details.bank')"
                    >
                    <label class="form-label">
                      {{ 'invoices_v2.form.bank' | translate }}
                    </label>
                    <ng-select
                      class="ng-select-custom ng-select-bank"
                      formControlName="bank"
                      [items]="banks"
                      bindLabel="__bankSelectLabel"
                      bindValue="bank_code"
                      [searchable]="true"
                      [searchFn]="bankSearchFn"
                      [clearable]="true"
                      [compareWith]="compareBankCode"
                      [loading]="banksLoading"
                      [disabled]="banksLoading"
                      appendTo="body"
                      dropdownPanelClass="invoice-v2-bank-dropdown-panel"
                      [placeholder]="'invoices_v2.form.select_bank' | translate"
                      (open)="onBankDropdownOpen(i)"
                      (close)="onBankDropdownClose(i)"
                    >
                      <ng-template ng-label-tmp let-item="item">
                        <span class="invoice-v2-bank-label" [attr.title]="getBankLabel(item)">
                          {{ getBankLabel(item) }}
                        </span>
                      </ng-template>
                      <ng-template ng-option-tmp let-item="item">
                        <span class="invoice-v2-bank-option-name" [attr.title]="getBankLabel(item)">
                          {{ getBankLabel(item) }}
                        </span>
                      </ng-template>
                    </ng-select>
                    <div *ngIf="banksLoading" class="form-helper">
                      <i class="fa fa-spinner fa-spin"></i>
                      {{ 'invoices_v2.form.banks_loading' | translate }}
                    </div>
                    <div *ngIf="!banksLoading && (banksLoadError || banks.length === 0)" class="form-helper form-no-customers">
                      <i class="fa fa-info-circle"></i>
                      {{ 'invoices_v2.form.banks_load_failed' | translate }}
                    </div>
                    </div>

                    <!-- Branch -->
                    <div
                      *ngIf="isBankDetailFieldVisible(payment.get('type')?.value, 'branch')"
                      class="form-group"
                      [attr.data-validation-field]="'payment_methods.' + i + '.details.branch'"
                      [class.has-validation-error]="hasFieldError('payment_methods.' + i + '.details.branch')"
                    >
                      <label class="form-label">
                        {{ 'invoices_v2.form.branch' | translate }}
                      </label>
                      <ng-select
                        class="ng-select-custom"
                        formControlName="branch"
                        [items]="getBranchesForPayment(i)"
                        bindLabel="__branchSelectLabel"
                        bindValue="__branchFormValue"
                        [searchable]="true"
                        [searchFn]="branchSelectSearchFn"
                        [clearable]="true"
                        [compareWith]="compareBranchCode"
                        appendTo="body"
                        [placeholder]="'invoices_v2.form.select_branch' | translate"
                      >
                      </ng-select>
                    </div>

                    <!-- Account -->
                    <div
                      *ngIf="isBankDetailFieldVisible(payment.get('type')?.value, 'account')"
                      class="form-group"
                      [attr.data-validation-field]="'payment_methods.' + i + '.details.account'"
                      [class.has-validation-error]="hasFieldError('payment_methods.' + i + '.details.account')"
                    >
                      <label class="form-label">
                        {{ 'invoices_v2.form.account' | translate }}
                      </label>
                      <input
                        type="text"
                        formControlName="account"
                        class="form-control-custom"
                        [placeholder]="'invoices_v2.form.enter_account' | translate"
                      />
                    </div>

                    <!-- Credit Card Code -->
                    <div
                      *ngIf="isFieldRequired(payment.get('type')?.value, 'credit_card_code')"
                      class="form-group"
                      [attr.data-validation-field]="'payment_methods.' + i + '.details.credit_card_code'"
                      [class.has-validation-error]="hasFieldError('payment_methods.' + i + '.details.credit_card_code')"
                    >
                      <label class="form-label">
                        {{ 'invoices_v2.form.credit_card_code' | translate }}
                        <span class="required-mark">*</span>
                      </label>
                      <input
                        type="text"
                        formControlName="credit_card_code"
                        class="form-control-custom"
                        [placeholder]="'invoices_v2.form.enter_credit_card_code' | translate"
                      />
                      <div
                        *ngIf="
                          payment.get('details')?.get('credit_card_code')?.invalid &&
                          payment.get('details')?.get('credit_card_code')?.touched
                        "
                        class="form-error"
                      >
                        <i class="fa fa-exclamation-circle"></i>
                        {{ 'invoices_v2.form.credit_card_code_required' | translate }}
                      </div>
                    </div>

                    <!-- Credit Card Name -->
                    <div
                      *ngIf="isFieldRequired(payment.get('type')?.value, 'credit_card_name')"
                      class="form-group"
                      [attr.data-validation-field]="'payment_methods.' + i + '.details.credit_card_name'"
                      [class.has-validation-error]="hasFieldError('payment_methods.' + i + '.details.credit_card_name')"
                    >
                      <label class="form-label">
                        {{ 'invoices_v2.form.credit_card_name' | translate }}
                        <span class="required-mark">*</span>
                      </label>
                      <select formControlName="credit_card_name" class="form-control-custom">
                        <option value="">
                          {{ 'invoices_v2.form.select_credit_card' | translate }}
                        </option>
                        <option
                          *ngFor="let ct of creditCardTypes"
                          [value]="ct.value"
                        >
                          {{ ct.label }}
                        </option>
                      </select>
                      <div
                        *ngIf="
                          payment.get('details')?.get('credit_card_name')?.invalid &&
                          payment.get('details')?.get('credit_card_name')?.touched
                        "
                        class="form-error"
                      >
                        <i class="fa fa-exclamation-circle"></i>
                        {{ 'invoices_v2.form.credit_card_name_required' | translate }}
                      </div>
                    </div>

                    <!-- Credit Type -->
                    <div
                      *ngIf="isFieldRequired(payment.get('type')?.value, 'credit_type')"
                      class="form-group"
                      [attr.data-validation-field]="'payment_methods.' + i + '.details.credit_type'"
                      [class.has-validation-error]="hasFieldError('payment_methods.' + i + '.details.credit_type')"
                    >
                      <label class="form-label">
                        {{ 'invoices_v2.form.credit_type' | translate }}
                        <span class="required-mark">*</span>
                      </label>
                      <select formControlName="credit_type" class="form-control-custom">
                        <option value="">
                          {{ 'invoices_v2.form.select_credit_type' | translate }}
                        </option>
                        <option
                          *ngFor="let pt of creditPaymentTypes"
                          [value]="pt.value"
                        >
                          {{ pt.label }}
                        </option>
                      </select>
                      <div
                        *ngIf="
                          payment.get('details')?.get('credit_type')?.invalid &&
                          payment.get('details')?.get('credit_type')?.touched
                        "
                        class="form-error"
                      >
                        <i class="fa fa-exclamation-circle"></i>
                        {{ 'invoices_v2.form.credit_type_required' | translate }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="items-add-row">
          <button type="button" class="btn-add-item" (click)="addPaymentMethod()">
            <i class="fa fa-plus"></i>
            {{ 'invoices_v2.form.add_payment' | translate }}
          </button>
        </div>
      </div>

      <!-- Notes Section -->
      <div class="form-section notes-section">
        <div class="section-header">
          <div class="section-icon" style="background: #64748b;">
            <i class="fa fa-sticky-note-o"></i>
          </div>
          <div>
            <h3 class="section-title">
              {{ 'invoices_v2.form.notes' | translate }}
            </h3>
            <p class="section-subtitle">
              {{ 'invoices_v2.form.notes_subtitle' | translate: documentTypeTranslateParams }}
            </p>
          </div>
        </div>

        <div class="form-group ckeditor-container" [class.rtl]="curentLang === 'ar' || curentLang === 'he'">
          <ckeditor
            [editor]="Editor"
            formControlName="invoice_note"
            [config]="editorConfig"
          ></ckeditor>
        </div>
      </div>

      <!-- Invoice Summary Section -->
      <div class="summary-section" *ngIf="hasItemsForSelectedType">
        <div class="section-header" style="border-bottom: none; padding-bottom: 0;">
          <div class="section-icon" style="background: #4f46e5;">
            <i class="fa fa-calculator"></i>
          </div>
          <div>
            <h3 class="section-title">
              {{ 'invoices_v2.form.invoice_summary' | translate: documentTypeTranslateParams }}
            </h3>
          </div>
        </div>

        <div
          class="summary-grid"
          [attr.dir]="curentLang === 'ar' || curentLang === 'he' ? 'rtl' : 'ltr'"
        >
          <div class="summary-item">
            <div class="summary-label">{{ 'invoices_v2.form.before_discount' | translate }}</div>
            <div class="summary-value">{{ calculatedAmounts.gross_before_discount | number: '1.2-2' }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">{{ 'invoices_v2.common.discount' | translate }}</div>
            <div class="summary-value">{{ calculatedAmounts.all_discount | number: '1.2-2' }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">{{ 'invoices_v2.form.after_discount' | translate }}</div>
            <div class="summary-value">{{ calculatedAmounts.amount_after_discount | number: '1.2-2' }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">{{ 'invoices_v2.form.vat_amount' | translate }}</div>
            <div class="summary-value">{{ calculatedAmounts.all_vat_amount | number: '1.2-2' }}</div>
          </div>

          <div class="summary-item summary-total">
            <div class="summary-label">{{ 'invoices_v2.common.total_amount' | translate }}</div>
            <div class="summary-value">{{ calculatedAmounts.total_amount | number: '1.2-2' }}</div>
          </div>
        </div>
      </div>

      <!-- Payments Summary Section -->
      <div class="summary-section payments-summary" *ngIf="paymentMethodsRequired">
        <div class="section-header" style="border-bottom: none; padding-bottom: 0;">
          <div class="section-icon" style="background: #10b981;">
            <i class="fa fa-credit-card"></i>
          </div>
          <div>
            <h3 class="section-title">
              {{ 'invoices_v2.form.payments_summary' | translate }}
            </h3>
          </div>
        </div>

        <div class="summary-grid">
          <div class="summary-item">
            <div class="summary-label">{{ 'invoices_v2.form.number_of_payments' | translate }}</div>
            <div class="summary-value">{{ getNonZeroPaymentCount() }}</div>
          </div>

          <div class="summary-item" *ngFor="let payment of getNonZeroPaymentControls()">
            <div class="summary-label">
              {{ getPaymentMethodName(payment.get('type')?.value) }}
            </div>
            <div class="summary-value">{{ getPaymentRowAmount(payment) | number: '1.2-2' }}</div>
          </div>

          <div class="summary-item summary-total">
            <div class="summary-label">{{ 'invoices_v2.form.total_payments' | translate }}</div>
            <div class="summary-value">{{ getTotalPayments() | number: '1.2-2' }}</div>
          </div>
        </div>
      </div>

      <!-- Tax authority reporting (above submit; hidden when document type cannot send, e.g. Receipt) -->
      <div class="form-section tax-authority-footer-section" *ngIf="showTaxAuthorityReportingPanel">
        <div class="tax-authority-panel">
          <div class="tax-authority-panel__head">
            <div class="tax-authority-panel__head-icon" aria-hidden="true">
              <i class="fa fa-university"></i>
            </div>
            <div class="tax-authority-panel__head-text">
              <h4 class="tax-authority-panel__title">
                {{ 'invoices_v2.form.tax_reporting_title' | translate }}
              </h4>
              <p class="tax-authority-panel__desc" *ngIf="isSendToTaxAuthorityCheckboxEnabled">
                {{ 'invoices_v2.form.tax_reporting_description' | translate }}
              </p>
            </div>
          </div>
          <div class="tax-authority-panel__control" id="tax-authority-send-checkbox-row">
            <label class="tax-authority-panel__checkbox-label">
              <input
                type="checkbox"
                id="tax-authority-send-checkbox"
                class="tax-authority-panel__checkbox"
                [checked]="invoiceForm.get('allocation_number')?.value === 1"
                [disabled]="!isSendToTaxAuthorityCheckboxEnabled"
                (change)="onSendToTaxAuthorityChange($event)"
              />
              <span class="tax-authority-panel__checkbox-text">{{ 'invoices_v2.view.request_allocation' | translate }}</span>
            </label>
          </div>
          <div class="tax-authority-panel__notice" *ngIf="!isSendToTaxAuthorityCheckboxEnabled" role="status">
            <i class="fa fa-info-circle" aria-hidden="true"></i>
            <ng-container [ngSwitch]="taxAuthorityDisabledNoticeKind">
              <span *ngSwitchCase="'max_missing'">{{ 'invoices_v2.form.send_to_tax_authority_max_missing_hint' | translate }}</span>
              <span *ngSwitchCase="'company'">{{ 'invoices_v2.form.send_to_tax_authority_disabled_company_hint' | translate }}</span>
              <span *ngSwitchDefault>{{ 'invoices_v2.form.send_to_tax_authority_disabled_hint' | translate }}</span>
            </ng-container>
          </div>
        </div>
      </div>

      <!-- Form footer: explicit finalize vs draft (no ambiguous checkbox) -->
      <div class="form-footer">
        <button
          type="button"
          class="btn-submit"
          [disabled]="isSubmitting"
          (click)="submitWithConfirmIntent(true)"
        >
          <i class="fa" [ngClass]="isSubmitting && submittingAsConfirm === true ? 'fa-spinner fa-spin' : 'fa-check'"></i>
          {{ 'invoices_v2.form.confirm_invoice' | translate: documentTypeTranslateParams }}
        </button>
        <button
          type="button"
          class="btn-draft"
          [disabled]="isSubmitting"
          (click)="submitWithConfirmIntent(false)"
        >
          <i class="fa" [ngClass]="isSubmitting && submittingAsConfirm === false ? 'fa-spinner fa-spin' : 'fa-file-o'"></i>
          {{
            isEditMode
              ? ('invoices_v2.form.save_invoice_draft' | translate: documentTypeTranslateParams)
              : ('invoices_v2.form.create_invoice_draft' | translate: documentTypeTranslateParams)
          }}
        </button>
        <button type="button" class="btn-cancel" [disabled]="isSubmitting" (click)="cancel()">
          <i class="fa fa-times"></i>
          {{ 'invoices_v2.common.cancel' | translate }}
        </button>
      </div>
    </form>
  </div>
</div>

<!-- Create flow: choose document type first (same options as invoice_type select) -->
<div
  *ngIf="showDocumentTypePicker"
  class="doc-type-picker-backdrop"
  role="dialog"
  aria-modal="true"
  [attr.aria-label]="'invoices_v2.form.document_type_picker_title' | translate"
>
  <div
    class="doc-type-picker-dialog"
    [dir]="curentLang === 'ar' || curentLang === 'he' ? 'rtl' : 'ltr'"
    (click)="$event.stopPropagation()"
  >
    <div class="doc-type-picker-header">
      <span class="doc-type-picker-title">
        <i class="fa fa-file-text-o"></i>
        {{ 'invoices_v2.form.document_type_picker_title' | translate }}
      </span>
    </div>
    <div class="doc-type-picker-body">
      <p class="doc-type-picker-hint">{{ 'invoices_v2.form.document_type_picker_hint' | translate }}</p>
      <div class="doc-type-picker-list">
        <button
          type="button"
          class="doc-type-picker-option"
          *ngFor="let type of invoiceTypes"
          (click)="selectDocumentTypeFromPicker(+type.id)"
        >
          <i class="fa fa-plus-circle doc-type-picker-option__icon"></i>
          <span class="doc-type-picker-option__label">{{ getInvoiceTypePickerLabel(type) }}</span>
          <i class="fa fa-chevron-left doc-type-picker-option__arrow" *ngIf="curentLang === 'ar' || curentLang === 'he'"></i>
          <i class="fa fa-chevron-right doc-type-picker-option__arrow" *ngIf="curentLang === 'en'"></i>
        </button>
      </div>
    </div>
    <div class="doc-type-picker-footer">
      <button type="button" class="doc-type-picker-back" (click)="cancelDocumentTypePicker()">
        <i class="fa fa-arrow-right" *ngIf="curentLang === 'ar' || curentLang === 'he'"></i>
        <i class="fa fa-arrow-left" *ngIf="curentLang === 'en'"></i>
        {{ 'invoices_v2.form.document_type_picker_back' | translate }}
      </button>
    </div>
  </div>
</div>

<!-- Create flow: merge existing documents after customer select -->
<div
  *ngIf="showCreateMergeModal"
  class="create-merge-backdrop"
  (click)="closeCreateMergeModal()"
>
  <div
    class="create-merge-dialog"
    [dir]="curentLang === 'ar' || curentLang === 'he' ? 'rtl' : 'ltr'"
    (click)="$event.stopPropagation()"
  >
    <div class="create-merge-header">
      <span class="create-merge-title">
        <i class="fa fa-compress"></i>
        {{ 'invoices_v2.form.create_merge_modal_title' | translate }}
      </span>
      <button type="button" class="create-merge-close" (click)="closeCreateMergeModal()">
        <i class="fa fa-times"></i>
      </button>
    </div>
    <div class="create-merge-body">
      <p class="create-merge-hint">{{ 'invoices_v2.form.create_merge_modal_hint' | translate }}</p>
      <div *ngIf="createMergeLoading" class="create-merge-loading">
        <div class="create-merge-spinner"></div>
      </div>
      <div *ngIf="!createMergeLoading" class="create-merge-list">
        <div *ngIf="createMergeInvoices.length === 0" class="create-merge-empty">
          {{ 'invoices_v2.form.create_merge_empty' | translate }}
        </div>
        <div
          *ngFor="let inv of createMergeInvoices"
          class="create-merge-row"
          [class.create-merge-row--selected]="isCreateMergeSelected(getCreateMergeRowId(inv))"
          [class.create-merge-row--muted]="!isCreateMergeRowSelectable(inv)"
        >
          <input
            type="checkbox"
            class="create-merge-cb"
            [checked]="isCreateMergeSelected(getCreateMergeRowId(inv))"
            [disabled]="!isCreateMergeRowSelectable(inv)"
            (change)="toggleCreateMergeSelection(getCreateMergeRowId(inv))"
          />
          <span class="create-merge-type">{{ getInvoiceTypeLabel(inv.invoice_type) }}</span>
          <span class="create-merge-doc">{{ inv.document_number || ('#' + getCreateMergeRowId(inv)) }}</span>
          <span class="create-merge-date">{{ inv.invoice_date | date: 'dd/MM/yyyy' }}</span>
          <span class="create-merge-amt">{{ getCreateMergeDisplayAmount(inv) | number:'1.2-2' }} ₪</span>
          <button
            type="button"
            class="create-merge-preview-btn"
            [title]="'invoices_v2.view.preview' | translate"
            (click)="openCreateMergeInvoicePreview(inv); $event.stopPropagation()"
          >
            <i class="fa fa-eye"></i>
          </button>
          <button
            type="button"
            class="create-merge-exclude-btn"
            [disabled]="isCreateMergeExcluding(getCreateMergeRowId(inv))"
            [title]="'invoices_v2.form.create_merge_exclude_btn' | translate"
            (click)="confirmExcludeFromCreateMerge(inv); $event.stopPropagation()"
          >
            <i class="fa" [ngClass]="isCreateMergeExcluding(getCreateMergeRowId(inv)) ? 'fa-spinner fa-spin' : 'fa-eye-slash'"></i>
          </button>
        </div>
      </div>
    </div>
    <div class="create-merge-footer">
      <button type="button" class="btn-cancel-merge" (click)="closeCreateMergeModal()">
        {{ 'invoices_v2.common.cancel' | translate }}
      </button>
      <button
        type="button"
        class="btn-run-merge"
        [disabled]="createMergeSelectedIds.length < 1 || createMergeLoading"
        (click)="executeCreateBulkMerge()"
      >
        <i class="fa fa-compress"></i>
        {{ 'invoices_v2.form.create_merge_execute' | translate }}
      </button>
    </div>
  </div>
</div>

<!-- Create-merge row: invoice HTML preview (above merge modal backdrop) -->
<div
  *ngIf="showCreateMergePreviewModal"
  class="create-merge-preview-backdrop"
  (click)="closeCreateMergeInvoicePreview()"
>
  <div
    class="create-merge-preview-dialog"
    [dir]="curentLang === 'ar' || curentLang === 'he' ? 'rtl' : 'ltr'"
    (click)="$event.stopPropagation()"
  >
    <div class="create-merge-preview-header">
      <span class="create-merge-preview-title">
        <i class="fa fa-eye"></i>
        {{ 'invoices_v2.view.preview' | translate }}
        <span *ngIf="createMergePreviewInvoiceLabel" class="create-merge-preview-docnum">
          — {{ createMergePreviewInvoiceLabel }}
        </span>
      </span>
      <div class="create-merge-preview-header-actions">
        <button
          type="button"
          class="create-merge-preview-select-btn"
          *ngIf="
            createMergePreviewInvoiceId != null &&
            !isCreateMergeSelected(createMergePreviewInvoiceId) &&
            isCreateMergePreviewSelectable()
          "
          (click)="toggleCreateMergeSelection(createMergePreviewInvoiceId!)"
        >
          <i class="fa fa-check-square-o"></i>
          {{
            curentLang === 'ar'
              ? 'تحديد للدمج'
              : curentLang === 'he'
                ? 'בחר למיזוג'
                : 'Select for merge'
          }}
        </button>
        <button
          type="button"
          class="create-merge-preview-deselect-btn"
          *ngIf="
            createMergePreviewInvoiceId != null &&
            isCreateMergeSelected(createMergePreviewInvoiceId) &&
            isCreateMergePreviewSelectable()
          "
          (click)="toggleCreateMergeSelection(createMergePreviewInvoiceId!)"
        >
          <i class="fa fa-minus-square-o"></i>
          {{
            curentLang === 'ar'
              ? 'إلغاء التحديد'
              : curentLang === 'he'
                ? 'בטל בחירה'
                : 'Deselect'
          }}
        </button>
        <button
          type="button"
          class="create-merge-preview-exclude-btn"
          *ngIf="createMergePreviewInvoiceId != null"
          [disabled]="isCreateMergeExcluding(createMergePreviewInvoiceId!)"
          (click)="confirmExcludeFromCreateMergeById(createMergePreviewInvoiceId!)"
        >
          <i class="fa" [ngClass]="isCreateMergeExcluding(createMergePreviewInvoiceId!) ? 'fa-spinner fa-spin' : 'fa-eye-slash'"></i>
          {{ 'invoices_v2.form.create_merge_exclude_btn' | translate }}
        </button>
        <button type="button" class="create-merge-preview-close" (click)="closeCreateMergeInvoicePreview()">
          <i class="fa fa-times"></i>
        </button>
      </div>
    </div>
    <div class="create-merge-preview-body">
      <div *ngIf="createMergePreviewLoading" class="create-merge-preview-loading">
        <div class="create-merge-spinner"></div>
      </div>
      <iframe
        *ngIf="!createMergePreviewLoading && createMergePreviewUrl"
        [src]="createMergePreviewUrl"
        class="create-merge-preview-iframe"
        title="Invoice preview"
        frameborder="0"
        scrolling="auto"
      ></iframe>
    </div>
  </div>
</div>

<!-- Add Customer modal -->
<app-add-customer-modal
  [showModal]="showAddCustomerModal"
  [mode]="'add'"
  [defaultCompanyId]="invoiceForm.get('company_id')?.value"
  (onClose)="onAddCustomerModalClose()"
  (onSave)="onAddCustomerSaved($event)"
></app-add-customer-modal>

<!-- Add Product modal -->
<p-dialog
  [(visible)]="showAddProductModal"
  [header]="'invoices_v2.form.add_new_product' | translate"
  [modal]="true"
  [style]="{ width: 'min(96vw, 1280px)', maxWidth: '100%' }"
  [contentStyle]="{ maxHeight: 'min(88vh, 860px)', overflowY: 'auto', overflowX: 'hidden' }"
  [draggable]="false"
  [resizable]="false"
  [maximizable]="true"
  [breakpoints]="{ '960px': '96vw', '576px': '100vw' }"
  (onHide)="onAddProductModalClose()"
  styleClass="add-product-dialog-responsive"
>
  <app-add-product-modal
    *ngIf="showAddProductModal"
    [companyId]="invoiceCompanyId"
    (productAdded)="onAddProductSaved($event)"
    (cancel)="onAddProductModalClose()"
  ></app-add-product-modal>
</p-dialog>
